import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { baseUrl } from '../../../constants';

@Injectable({ providedIn: 'root' })
export class VehicleService {
  private baseUrl = `${baseUrl}Vehicles`;

  constructor(private http: HttpClient) {}

  private get headers() {
    const token = localStorage.getItem('token');
    return {
      headers: new HttpHeaders({
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      })
    };
  }

  /** ✅ Get all vehicles */
  getVehicles(): Observable<any[]> {
    console.log('%c[VehicleService] 🚚 Fetching vehicles...', 'color: dodgerblue');
    return this.http.get<any[]>(this.baseUrl, this.headers).pipe(
      tap({
        next: res => console.log('%c[VehicleService] ✅ Vehicles loaded:', 'color: green', res),
        error: err => console.error('[VehicleService] ❌ Failed to load vehicles:', err)
      })
    );
  }

  /** ✅ Get by id */
  getVehicleById(id: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/${id}`, this.headers);
  }

  /** ✅ Create (send only fields the backend expects) */
  createVehicle(vehicle: { plateNumber: string; model: string; type: string; capacity: any }): Observable<any> {
    const payload = {
      plateNumber: vehicle.plateNumber,
      model: vehicle.model,
      type: vehicle.type,
      capacity: String(vehicle.capacity) // backend expects string in your service
    };
    console.log('%c[VehicleService] ➕ Creating vehicle with payload:', 'color: purple', payload);

    return this.http.post(`${this.baseUrl}`, payload, {
      ...this.headers,
      observe: 'response',
      responseType: 'text'
    } as any);
  }

  /** ✅ Update (IsActive is allowed on update DTO) */
  updateVehicle(id: number, vehicle: any): Observable<any> {
    const payload = {
      plateNumber: vehicle.plateNumber,
      model: vehicle.model,
      type: vehicle.type,
      capacity: String(vehicle.capacity),
      isActive: !!vehicle.isActive
    };
    console.log('%c[VehicleService] ✏️ Updating vehicle:', 'color: orange', { id, payload });

    return this.http.put(`${this.baseUrl}/${id}`, payload, {
      ...this.headers,
      observe: 'response',
      responseType: 'text'
    } as any);
  }

  /** ✅ Delete */
  deleteVehicle(id: number): Observable<any> {
    console.log('%c[VehicleService] 🗑️ Deleting vehicle:', 'color: red', id);
    return this.http.delete(`${this.baseUrl}/${id}`, {
      ...this.headers,
      observe: 'response',
      responseType: 'text'
    } as any);
  }

  /** ✅ Assign vehicle to rider */
  assignVehicle(vehicleId: number, riderId: number): Observable<any> {
    const payload = { vehicleId, riderId };
    console.log('%c[VehicleService] 🔗 Assign vehicle:', 'color: teal', payload);
    return this.http.post(`${this.baseUrl}/assign`, payload, {
      ...this.headers,
      observe: 'response',
      responseType: 'text'
    } as any);
  }

  getMeterAnalytics(workDate?: string): Observable<any> {
    const query = workDate ? `?workDate=${encodeURIComponent(workDate)}` : '';
    return this.http.get<any>(`${this.baseUrl}/meter-analytics${query}`, this.headers);
  }

  openWork(vehicleId: number, meterReading: number, fuelLevel: number, workDate?: string): Observable<any> {
    const payload: any = { meterReading, fuelLevel };
    if (workDate) payload.workDate = workDate;
    return this.http.post(`${this.baseUrl}/${vehicleId}/work-open`, payload, this.headers);
  }

  closeWork(vehicleId: number, meterReading: number, fuelLevel: number, workDate?: string): Observable<any> {
    const payload: any = { meterReading, fuelLevel };
    if (workDate) payload.workDate = workDate;
    return this.http.post(`${this.baseUrl}/${vehicleId}/work-close`, payload, this.headers);
  }

  editMeterLog(
    meterLogId: number,
    payload: { startMeter?: number; endMeter?: number; startFuelLevel?: number; endFuelLevel?: number }
  ): Observable<any> {
    return this.http.put(`${this.baseUrl}/meter-logs/${meterLogId}`, payload, this.headers);
  }
}
